// Copyright ©️ 2026 eVolve MEP, LLC

import { Stack } from '@mantine/core';

import { PillSelect } from 'components/Mantine/PillSelect';
import { isNil } from 'helpers/isNotNil';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { useKeepCompatibleBender } from 'modules/Shop/EvolveFabrication/BendCreator/State/useKeepCompatibleBender';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

/** In feet. */
const STOCK_LENGTH_OPTIONS = [
  { value: '10', label: "10'" },
  { value: '20', label: "20'" },
] as const;

export const MaterialStep = () => {
  const { conduitTypes, conduitSizes } = useConduits();
  const form = useBendCreatorFormContext();
  const { scheduleValidation } = useBendCreatorValidationContext();
  const keepCompatibleBender = useKeepCompatibleBender();
  const { conduitTypeId, conduitSizeId, materialStockLength } = form.values;

  return (
    <Stack gap={20}>
      <PillSelect
        label="Material"
        required
        data={conduitTypes?.map(({ conduitTypeId: value, name }) => ({ value, label: name })) ?? []}
        error={form.errors.conduitTypeId}
        value={conduitTypeId}
        onChange={(newConduitTypeId) => {
          form.setFieldValue('conduitTypeId', newConduitTypeId);
          void keepCompatibleBender(newConduitTypeId, conduitSizeId);
          scheduleValidation('conduitTypeId');
        }}
      />
      <PillSelect
        label="Conduit Size"
        required
        mono
        wrap
        data={conduitSizes?.map(({ conduitSizeId: value, name }) => ({ value, label: name })) ?? []}
        error={form.errors.conduitSizeId}
        value={conduitSizeId}
        onChange={(newConduitSizeId) => {
          form.setFieldValue('conduitSizeId', newConduitSizeId);
          void keepCompatibleBender(conduitTypeId, newConduitSizeId);
          scheduleValidation('conduitSizeId');
        }}
      />
      <PillSelect
        label="Stock Length"
        required
        mono
        data={STOCK_LENGTH_OPTIONS}
        error={form.errors.materialStockLength}
        value={isNil(materialStockLength) ? null : String(materialStockLength)}
        onChange={(stockLength) => {
          form.setFieldValue('materialStockLength', isNil(stockLength) ? null : parseInt(stockLength, 10));
          scheduleValidation('materialStockLength');
        }}
      />
    </Stack>
  );
};
